<template>
  <div v-if="user" class="avatar-new-container" :class="'avatar--' + size">
    <div
      class="avatar-new"
      :class="{'clickable': canStartConversation(user)}"
      @click="startConversation(user, 'announcementHeader.startConversation')"
    >
      <div
        :style="backgroundImageStyle"
        :class="(!!backgroundImageStyle) ? 'has-image' : 'has-no-image'"
        v-tooltip.right="canStartConversation(user) ? 'Start conversation with ' + user.name : (user.id === userId ? 'You': '')"
        class="avatar-image"
      >
      </div>
    </div>
    <div class="avatar-new-user" style="align-self: center; margin-left: 0.5rem;" v-if="showDetails">
      <h4><strong>{{ user.name }}</strong></h4>
      <span class="avatar-new-user-department" v-if="departmentName && showDepartment">{{ departmentName }}</span>
      <span class="avatar-new-user-department" v-if="claims && showClaim">
        <span v-for="claim in claims">
          <a @click.stop="goToActor(claim.id)">{{ claim.name }}</a>
        </span>
      </span>
    </div>
  </div>
</template>
<script lang="ts">
  import { defineComponent } from 'vue'
  import { ConversationsMixin } from '@/util/ConversationsMixin'
  import UserMixin from '@/util/UserMixin'
  import { trackHeapEvent } from '@/util/analytics'
  import { Conversations } from '@/api/conversations'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin'

  export default defineComponent({
    name: "AvatarNew",
    mixins: [ConversationsMixin, UserMixin, TaxonomyMixin],
    props: {
      user: {
        type: Object,
        required: true,
      },
      showDetails: {
        type: Boolean,
        required: false,
        default: false,
      },
      showDepartment: {
        type: Boolean,
        required: false,
        default: false,
      },
      showClaim: {
        type: Boolean,
        required: false,
        default: false,
      },
      size: {
        type: String,
        validator: val => ['small', 'large'].includes(val)
      }
    },
    computed: {
      backgroundImageStyle() {
        return `background-image: url(${this.user.photoUrl || this.user.photo_url})`
      },
      departmentName() {
        if (!this.user.department) return ''

        const department = this.$store.state.taxonomies.departments.find(d => d.id === Number(this.user.department))
        if (!department) return this.user.department

        return this.getTranslatedTaxonomyName(department)
      },
      claims() {
        let claims = []
        if (this.user.id === this.profile.id) {
          claims = this.claimedActors
        } else {
          claims = this.user.latestClaim || this.user.claims
        }

        if (!claims || !claims.length) return []

        return claims.slice(0, 1).map(function (claim) {
          return {
            id: claim.id || claim.elastic_id,
            name: claim.name,
          }
        })
      },
    },
    methods: {
      goToActor(id: string) {
        this.$router.push(`/actors/${id}`)
      },
      canStartConversation(user) {
        if (!this.isLoggedIn) {
          return false
        }

        if (!this.hasAccessToConversations) {
          return false
        }

        if (user.is_deleted) {
          return false
        }

        if (user.id === this.userId) {
          return false
        }

        return this.$store.getters.canActorsBeContacted || this.isOwner
      },
      startConversation(user, trackHeap = 'announcementDetail.clickConversation') {
        if (!this.canStartConversation(user) || this.userId === user.id) {
          return
        }

        trackHeapEvent(trackHeap)

        Conversations.post({ recipient_id: user.id }).then((conversation) => {
          this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
          this.$router.push('/profile/conversations/' + conversation.id).then(() => {
            this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
              component: 'user-information-side-panel',
              metaData: {
                userId: user.id,
                allowDelete: false,
              },
            })
          })
        })
      },
    }
  })
</script>

<style scoped lang="scss">
  .avatar--small {
    .avatar-new {
      width: 25px;
      height: 25px;

    }

    .avatar-new-user {
      h4 {
        font-size: 12px !important;
      }

      span {
        font-size: 10px !important;
      }
    }
  }

  .avatar--large {
    .avatar-new {
      width: 80px;
      height: 80px;

    }

    .avatar-new-user {
      h4 {
        font-size: 12px !important;
      }

      span {
        font-size: 10px !important;
      }
    }
  }


  .avatar-new {
    cursor: pointer;
    position: relative;
    width: 35px;
    height: 35px;

    .avatar-image {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-size: contain;
      background-repeat: no-repeat;
    }
  }

  .avatar-new-container {
    display: flex;
    flex-direction: row;
  }

  .avatar-new-user {
    display: flex;
    flex-direction: column;

    .avatar-new-user-department {
      color: var(--primary-community-dark);
      font-size: 11px;
    }
  }
</style>
